/**
 * Profile Card Slide Generators for PowerPoint Export
 * Generates profile summary cards in a 2x2 grid layout
 * Used for Top Profiles by Reach and Top Profiles by Owned slides
 */

import {
  COLOR_FEED_CARD_BG,
  COLOR_FEED_CARD_BORDER,
  COLOR_FEED_PROFILE_NAME,
  COLOR_METRIC_DEFAULT,
  COLOR_METRIC_EARNED,
  COLOR_METRIC_OWNED,
  COLOR_PROFILE_BIO,
  COLOR_PROFILE_LABELS,
} from 'common/Card/ppt-constants';

import { PROFILE_CARD_LAYOUT } from 'common/utils/ppt/chartLayoutConstants';
import { sanitizeUrlForPPT } from './urlSanitizer';

// Utility function to format numbers with thousands separators
const formatNumberWithCommas = num => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString('en-US');
};

// Helper to get Twitter/X URL - API provides twitterUser, not twitterURL
const getTwitterURL = profile => {
  if (profile.twitterURL) return profile.twitterURL;
  if (profile.twitterUser) return `https://x.com/${profile.twitterUser}`;
  return null;
};

// Helper to get TikTok URL - API uses tikTokURL (capital T), UI uses tiktokURL
const getTikTokURL = profile => profile.tikTokURL || profile.tiktokURL || null;

// Social media platforms configuration
// Order matches UI: ProfileDetails.jsx in Social Profile Details page
const SOCIAL_PLATFORMS = [
  { urlKey: 'twitter', icon: 'twitter.png', tooltip: 'Twitter/X Profile', getUrl: getTwitterURL },
  { urlKey: 'blueskyURL', icon: 'bluesky.png', tooltip: 'Bluesky Profile' },
  { urlKey: 'linkedInURL', icon: 'linkedIn.png', tooltip: 'LinkedIn Profile' },
  { urlKey: 'youtubeURL', icon: 'youtube.png', tooltip: 'YouTube Channel' },
  { urlKey: 'facebookURL', icon: 'facebook.png', tooltip: 'Facebook Profile' },
  { urlKey: 'instagramURL', icon: 'instagram.png', tooltip: 'Instagram Profile' },
  { urlKey: 'redditURL', icon: 'reddit.png', tooltip: 'Reddit Profile' },
  { urlKey: 'tiktok', icon: 'tiktok.png', tooltip: 'TikTok Profile', getUrl: getTikTokURL },
  { urlKey: 'tumblrURL', icon: 'tumblr.png', tooltip: 'Tumblr Profile' },
  { urlKey: 'clinicURL', icon: 'hospital.png', tooltip: 'Clinic/Institution Profile' },
  { urlKey: 'otherURL', icon: 'web.png', tooltip: 'Other Links' },
];

/**
 * Add social media icons to a profile card in the top-right corner
 * Uses pre-fetched icon base64 data to avoid multiple server requests
 *
 * @param {Object} slide - PptxGenJS slide object
 * @param {Object} profile - Profile data object with social URLs
 * @param {number} cardX - Card X position
 * @param {number} cardY - Card Y position
 * @param {number} cardW - Card width
 * @param {Map|null} socialIconsMap - Pre-fetched map of icon filename -> base64 data
 */
const addSocialIconsToCard = (slide, profile, cardX, cardY, cardW, socialIconsMap = null) => {
  if (!profile) return;

  const iconSize = 0.2; // Smaller icons for card (was 0.25 in profile overview)
  const iconSpacing = 0.25; // Tighter spacing
  const rightPadding = 0.15; // Padding from card edge

  // Collect all available social links (no limit)
  // Use getUrl function if available, otherwise check profile[urlKey]
  const availableLinks = SOCIAL_PLATFORMS.filter(platform => {
    if (platform.getUrl) return platform.getUrl(profile);
    return profile[platform.urlKey];
  });

  if (availableLinks.length === 0) return;

  // Calculate starting X position (right-aligned within card)
  const totalWidth = availableLinks.length * iconSpacing - (iconSpacing - iconSize);
  const startX = cardX + cardW - rightPadding - totalWidth;
  const startY = cardY + 0.15; // Align with top of card content

  // Add icons
  availableLinks.forEach((platform, index) => {
    const iconX = startX + index * iconSpacing;

    // Get URL using getUrl function if available, otherwise from profile
    const url = platform.getUrl ? platform.getUrl(profile) : profile[platform.urlKey];

    try {
      // Use pre-fetched base64 data if available, otherwise fall back to path
      const iconBase64 = socialIconsMap?.get(platform.icon);

      if (iconBase64) {
        // Use pre-fetched base64 data (more efficient)
        slide.addImage({
          data: iconBase64,
          x: iconX,
          y: startY,
          w: iconSize,
          h: iconSize,
          hyperlink: { url: sanitizeUrlForPPT(url), tooltip: platform.tooltip },
        });
      } else {
        // Fallback to path-based loading
        slide.addImage({
          path: `/ppt-export/social-icons/${platform.icon}`,
          x: iconX,
          y: startY,
          w: iconSize,
          h: iconSize,
          hyperlink: { url: sanitizeUrlForPPT(url), tooltip: platform.tooltip },
        });
      }
    } catch (error) {
      // Continue if icon addition fails
    }
  });
};

/**
 * Render a single profile card at the specified position
 * Reusable for both Project Export profile slides and potential Profile Export refactoring
 *
 * @param {Object} slide - PptxGenJS slide object
 * @param {Object} pptx - PptxGenJS instance
 * @param {Object} profile - Profile data object
 * @param {string|null} avatarBase64 - Base64 encoded avatar image or null
 * @param {number} x - X position in inches
 * @param {number} y - Y position in inches
 * @param {number} w - Card width in inches
 * @param {number} h - Card height in inches
 * @param {Map|null} socialIconsMap - Pre-fetched map of icon filename -> base64 data
 */
export const renderProfileCard = (
  slide,
  pptx,
  profile,
  avatarBase64,
  x,
  y,
  w,
  h,
  socialIconsMap = null,
) => {
  const { AVATAR_SIZE, BIO_MAX_CHARS } = PROFILE_CARD_LAYOUT;

  // Card background with rounded corners (matches feed card style)
  slide.addShape(pptx.ShapeType.roundRect, {
    x,
    y,
    w,
    h,
    fill: { color: COLOR_FEED_CARD_BG },
    line: { color: COLOR_FEED_CARD_BORDER, width: 0.75, transparency: 47 },
    rectRadius: 0.1,
  });

  // Social media icons in top-right corner (clickable links)
  addSocialIconsToCard(slide, profile, x, y, w, socialIconsMap);

  // Avatar (circular) - with fallback placeholder
  if (avatarBase64) {
    try {
      // Ensure the base64 string is properly formatted
      let imageData = avatarBase64;
      if (!imageData.startsWith('data:')) {
        imageData = `data:image/png;base64,${imageData}`;
      }

      slide.addImage({
        data: imageData,
        x: x + 0.15,
        y: y + 0.15,
        w: AVATAR_SIZE,
        h: AVATAR_SIZE,
        rounding: true,
      });
    } catch (error) {
      // Fallback to placeholder circle on error
      slide.addShape('ellipse', {
        x: x + 0.15,
        y: y + 0.15,
        w: AVATAR_SIZE,
        h: AVATAR_SIZE,
        fill: { color: 'E0E0E0' },
      });
    }
  } else {
    // Fallback placeholder circle
    slide.addShape('ellipse', {
      x: x + 0.15,
      y: y + 0.15,
      w: AVATAR_SIZE,
      h: AVATAR_SIZE,
      fill: { color: 'E0E0E0' },
    });
  }

  // Name (bold, next to avatar)
  slide.addText(profile.name || profile.profileName || 'Unknown', {
    x: x + AVATAR_SIZE + 0.3,
    y: y + 0.15,
    w: w - AVATAR_SIZE - 0.5,
    h: 0.25,
    fontSize: 11,
    bold: true,
    color: COLOR_FEED_PROFILE_NAME,
    valign: 'middle',
  });

  // Country (below name)
  const countryText = profile.countryName || profile.country || '';
  if (countryText) {
    slide.addText(countryText, {
      x: x + AVATAR_SIZE + 0.3,
      y: y + 0.4,
      w: w - AVATAR_SIZE - 0.5,
      h: 0.2,
      fontSize: 9,
      color: COLOR_PROFILE_LABELS,
      valign: 'middle',
    });
  }

  // Bio (below avatar, with dynamic font sizing based on content length)
  const rawBio = profile.twitterBio || profile.bio || '';

  // Sanitize bio text - remove problematic characters
  const sanitizedBio = rawBio
    .replace(/\u00A0/g, ' ') // Replace non-breaking spaces
    .replace(/[\u2018\u2019]/g, "'") // Replace smart quotes
    .replace(/[\u201C\u201D]/g, '"') // Replace smart double quotes
    .replace(/\u2013/g, '-') // Replace en dash
    .replace(/\u2014/g, '--') // Replace em dash
    .replace(/\u2026/g, '...') // Replace ellipsis character
    .replace(/[\u007F]/g, '') // Remove DEL character
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0001-\u0008\u000B-\u000C\u000E-\u001F]/g, '') // Remove control chars
    .replace(/\t+/g, ' ') // Replace tabs with space
    .replace(/\n\s*\n/g, '\n') // Remove empty lines
    .trim();

  // Dynamic font sizing based on bio length (similar to feed cards approach)
  let bioFontSize = 9; // Default
  let maxBioChars = BIO_MAX_CHARS;

  if (sanitizedBio.length <= 80) {
    bioFontSize = 10; // Short bios - larger font
    maxBioChars = 150;
  } else if (sanitizedBio.length <= 120) {
    bioFontSize = 9.5; // Medium-short bios
    maxBioChars = 180;
  } else if (sanitizedBio.length <= 180) {
    bioFontSize = 9; // Medium bios - standard
    maxBioChars = 220;
  } else if (sanitizedBio.length <= 250) {
    bioFontSize = 8.5; // Medium-long bios
    maxBioChars = 280;
  } else {
    bioFontSize = 8; // Long bios - smaller font to fit more
    maxBioChars = 350;
  }

  // Truncate bio to calculated max chars
  const bioText = sanitizedBio.substring(0, maxBioChars);
  const displayBio = bioText + (sanitizedBio.length > maxBioChars ? '...' : '');

  if (displayBio) {
    slide.addText(displayBio, {
      x: x + 0.15,
      y: y + 0.95,
      w: w - 0.3,
      h: 0.9,
      fontSize: bioFontSize,
      color: COLOR_PROFILE_BIO,
      wrap: true,
      valign: 'top',
      shrinkText: true, // Enable PPT's auto-shrink as fallback
    });
  } else {
    // Show "No Bio" in italics when no bio is available
    slide.addText('No Bio', {
      x: x + 0.15,
      y: y + 0.95,
      w: w - 0.3,
      h: 0.3,
      fontSize: 9,
      italic: true,
      color: COLOR_PROFILE_LABELS, // Lighter color to indicate placeholder
    });
  }

  // Stats row at bottom: Contributions | Reach | HCP Connections | Owned | Earned
  // Match top padding (0.15) - stats height is ~0.43 (0.25 value + 0.18 label)
  const statsY = y + h - 0.58;
  const availableWidth = w - 0.3; // Total width minus padding

  // Only Earned (Orange) and Owned (Green) are colored to match Classic app conventions
  // Custom widths: HCP Connections needs more space, Owned/Earned right-aligned
  const stats = [
    {
      label: 'Contributions',
      value: profile.totalContributions || 0,
      color: COLOR_METRIC_DEFAULT,
      width: availableWidth * 0.18,
    },
    {
      label: 'Reach',
      value: profile.reachEstimate || profile.reach || 0,
      color: COLOR_METRIC_DEFAULT,
      width: availableWidth * 0.18,
    },
    {
      label: 'HCP Connections',
      value: profile.hcpInteractions || profile.hcpConnections || 0,
      color: COLOR_METRIC_DEFAULT,
      width: availableWidth * 0.24, // Wider for longer label
    },
    {
      label: 'Owned',
      value: profile.ownedContributions || profile.owned || 0,
      color: COLOR_METRIC_OWNED,
      width: availableWidth * 0.2,
    },
    {
      label: 'Earned',
      value: profile.earnedContributions || profile.earned || 0,
      color: COLOR_METRIC_EARNED,
      width: availableWidth * 0.2,
    },
  ];

  let currentX = x + 0.15;
  stats.forEach(stat => {
    // Value with color - 12pt to prevent large numbers from wrapping
    slide.addText(formatNumberWithCommas(stat.value), {
      x: currentX,
      y: statsY,
      w: stat.width,
      h: 0.25,
      fontSize: 12, // Reduced from 14 to 12 to prevent large values wrapping
      bold: true,
      color: stat.color,
    });
    // Label below value - 9pt for readability
    slide.addText(stat.label, {
      x: currentX,
      y: statsY + 0.25,
      w: stat.width,
      h: 0.18,
      fontSize: 9, // Reduced from 10 to 9 for better fit
      color: COLOR_PROFILE_LABELS,
    });
    currentX += stat.width;
  });
};

/**
 * Generate multiple profile card slides (2x2 grid)
 * Creates as many slides as needed for the provided profiles
 * Handles empty/incomplete data gracefully - no empty cards rendered
 *
 * @param {Object} pptx - PptxGenJS instance
 * @param {Array} profiles - Array of profile objects to display
 * @param {Map|Object} avatarMap - Map or object of profileUrl -> base64 avatar data
 * @param {Function} addSlideHeader - Function to add slide header
 * @param {Function} addSlideFooter - Function to add slide footer
 * @param {string} slideTitle - Title for the slides (e.g., 'Top Profiles | Reach')
 * @param {string|null} defaultAvatarBase64 - Default avatar to use when profile has no avatar
 * @param {Map|null} socialIconsMap - Pre-fetched map of icon filename -> base64 data
 * @returns {void}
 */
export const generateProfileCardSlides = (
  pptx,
  profiles,
  avatarMap,
  addSlideHeader,
  addSlideFooter,
  slideTitle = 'Top Profiles by Reach',
  defaultAvatarBase64 = null,
  socialIconsMap = null,
) => {
  // Validate inputs - skip if no profiles
  if (!profiles || !Array.isArray(profiles) || profiles.length === 0) {
    return;
  }

  const { CARD_WIDTH, CARD_HEIGHT, H_SPACING, V_SPACING, START_X, START_Y, ITEMS_PER_SLIDE } =
    PROFILE_CARD_LAYOUT;

  // Process profiles in chunks of ITEMS_PER_SLIDE (4)
  for (let i = 0; i < profiles.length; i += ITEMS_PER_SLIDE) {
    const chunk = profiles.slice(i, i + ITEMS_PER_SLIDE);

    // Only process non-empty chunks
    if (chunk.length > 0) {
      const slide = pptx.addSlide({ masterName: 'CONTENT_SLIDE' });

      addSlideHeader(slide, slideTitle);
      addSlideFooter(slide);

      // Render each profile card in the chunk
      chunk.forEach((profile, j) => {
        const row = Math.floor(j / 2);
        const col = j % 2;
        const x = START_X + col * (CARD_WIDTH + H_SPACING);
        const y = START_Y + row * (CARD_HEIGHT + V_SPACING);

        // Get avatar from map - handle both Map and plain object
        // Note: KOLs endpoint uses profileImageURL, feed items use profileUrl
        let avatarBase64 = null;
        const profileUrl = profile.profileImageURL || profile.profileUrl || profile.avatarUrl;

        if (profileUrl && avatarMap) {
          if (avatarMap instanceof Map) {
            avatarBase64 = avatarMap.get(profileUrl);
          } else if (typeof avatarMap === 'object') {
            avatarBase64 = avatarMap[profileUrl];
          }
        }

        // Use default avatar if no specific avatar found
        if (!avatarBase64) {
          avatarBase64 = defaultAvatarBase64;
        }

        renderProfileCard(
          slide,
          pptx,
          profile,
          avatarBase64,
          x,
          y,
          CARD_WIDTH,
          CARD_HEIGHT,
          socialIconsMap,
        );
      });
    }
  }
};
